import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, map, shareReplay } from 'rxjs';

/**
 * Authorization configuration interface from config.json
 */
export interface AuthorizationConfig {
  useMockGraph?: boolean;
  /**
   * When enabled, user roles (AAD group names like AI_MODE_Admin) are taken from config.json
   * instead of being fetched from backend.
   */
  useMockUserRoles?: boolean;
  /**
   * Mocked AAD role-group names for current user (e.g. ["AI_MODE_Admin"]).
   */
  mockUserRoles?: string[];
  useMockPrivileges?: boolean;
  /**
   * Path to mock role->privileges mapping file used by Administration tooling.
   */
  mockPrivilegesPath?: string;
  /**
   * Path to mock merged user privileges file (used when useMockPrivileges=true).
   */
  mockUserPrivilegesPath?: string;
}

export interface AssessmentConfig {
  coverageThresholdPercent?: number;
}

/**
 * Complete application configuration interface
 */
export interface AppConfig {
  autoSignIn?: boolean;
  popupForLogin?: boolean;
  cacheLocation?: string;
  instance?: string;
  tenantId?: string;
  clientId?: string;
  redirectUri?: string;
  oidcScopes?: string[];
  graphScopes?: string[];
  authorization?: AuthorizationConfig;
  assessment?: AssessmentConfig;
}

/**
 * Service for loading and managing application configuration
 */
@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private config$: Observable<AppConfig> | null = null;
  private configCache: AppConfig | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Loads configuration from assets/config.json
   */
  public loadConfig(): Observable<AppConfig> {
    if (this.config$) {
      return this.config$;
    }

    this.config$ = this.http.get<AppConfig>('assets/config.json').pipe(
      map((config) => {
        this.configCache = config;
        return config;
      }),
      catchError(error => {
        console.error('ConfigService: Failed to load configuration', error);
        // Return default configuration
        const defaultConfig: AppConfig = {
          authorization: {
            useMockGraph: false,
            useMockUserRoles: false,
            useMockPrivileges: false
          }
        };
        this.configCache = defaultConfig;
        return of(defaultConfig);
      }),
      shareReplay(1) // Cache the result
    );

    return this.config$;
  }

  /**
   * Returns authorization configuration (synchronously)
   * Must be called after loadConfig()
   */
  public getAuthorizationConfig(): AuthorizationConfig {
    if (!this.configCache) {
      return {
        useMockGraph: false,
        useMockUserRoles: false,
        useMockPrivileges: false
      };
    }

    return this.configCache.authorization || {
      useMockGraph: false,
      useMockUserRoles: false,
      useMockPrivileges: false
    };
  }

  /**
   * Returns full configuration (synchronously)
   */
  public getConfig(): AppConfig | null {
    return this.configCache;
  }
}
